import React, { useEffect, useState, useCallback } from 'react';
import ApiService from '../../../_services/ApiService';
import DashboardAgendaView from '../../instructor-dashboard/components/DashboardAgendaView';
import { UnifiedSlot } from '../../instructor-dashboard/InstructorDashboard';
import { openAttendanceForBatches } from '../../../utils/attendance-nav';

/**
 * Admin "Conducted View" tab on TemplateDetail. Fetches the template-scoped
 * dashboard endpoint and renders the same DashboardAgendaView used by the
 * instructor at /my-teaching-plan — so admins see the identical Planned |
 * Conducted layout, attendance rate, and Zoom sync icons per slot.
 *
 * The endpoint returns UnifiedSlot[] but with lesson-plan fields as null
 * (instructor-centric lookup doesn't map to a template-wide view; TODO).
 * DashboardAgendaView already renders gracefully when those are null.
 *
 * Admin-side actions on the planned column (SlotActionBar) are passed as
 * no-op stubs for now — admins interact with slots through the existing
 * "Manage Slots" tab. The Conducted column's actions (View Attendance,
 * Add Resource, Sync Attendance, Sync Video) are internal to
 * DashboardAgendaView and continue to work.
 */

interface Props {
  templateId: number;
}

interface DashboardResponse {
  success: boolean;
  data: {
    template: { id: number; name: string; publishStatus: string; courseId: number | null; courseName: string };
    // Filter reflects what the caller passed; null on either side = no clipping
    // applied on that side. By default nothing is passed and both are null,
    // meaning "all slots in this timetable template."
    filter: { startDate: string | null; endDate: string | null };
    slots: UnifiedSlot[];
    analytics: {
      totalPlanned: number;
      totalScheduled: number;
      totalConducted: number;
      totalCancelled: number;
      upcomingToday: number;
      totalHours: number;
      conductedHours: number;
      averageAttendance: number | null;
    };
  };
}

const noop = () => { /* admin flow uses the Manage Slots tab; these are placeholders */ };

const TemplateConductedView: React.FC<Props> = ({ templateId }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardResponse['data'] | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getTemplateDashboard(templateId);
      const body = response?.data as DashboardResponse | undefined;
      if (!body?.success) {
        throw new Error('Server responded without success flag');
      }
      setData(body.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load template dashboard';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #666)' }}>
        Loading conducted view…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 12, color: 'var(--status-error, #c62828)' }}>
          Failed to load: {error}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => fetchDashboard()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.slots.length === 0) {
    return (
      <div style={{ padding: 24, color: 'var(--text-muted, #666)' }}>
        No activity slots found for this timetable template.
      </div>
    );
  }

  const { slots, analytics } = data;
  // "Planned" = slots that still show only the PLANNED badge in the conducted
  // column (no actual activityslot row exists yet).
  // "Conducted" = slots that HAVE an actual activityslot row — matches what the
  // conducted column visually populates, regardless of whether the row's status
  // has been flipped to Conducted yet (Scheduled + Pre-Scheduled + Conducted).
  const conductedCount = analytics.totalScheduled;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          padding: '12px 16px',
          marginBottom: 12,
          background: 'var(--surface-card, #f8f9fa)',
          border: '1px solid var(--border-default, #e0e0e0)',
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        <div><strong>Total slots:</strong> {slots.length}</div>
        <div><strong>Planned:</strong> {analytics.totalPlanned}</div>
        <div><strong>Conducted:</strong> {conductedCount}</div>
      </div>

      <DashboardAgendaView
        slots={slots}
        onDayClick={noop}
        onAttendance={openAttendanceForBatches}
        onLessonPlan={noop}
        onViewTemplate={noop}
        onMeetingStarted={fetchDashboard}
        hideViewTemplateButton
      />
    </div>
  );
};

export default TemplateConductedView;
